import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Lightning, LockKey } from "@phosphor-icons/react";
import { MaskedLine } from "./Reveal";
import { useLiveRate, rateRangeText } from "@/lib/useLiveRate";

const EASE = [0.16, 1, 0.3, 1];

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: EASE },
});

export function PhoneMockup() {
  return (
    <div className="relative w-[270px] sm:w-[300px]" data-testid="app-mockup">
      <div className="absolute -inset-10 rounded-full bg-brand-500/20 blur-3xl" aria-hidden="true" />
      <div className="animate-float-slow relative rounded-[2.6rem] border border-line bg-pine p-2.5 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.7)]">
        <div className="overflow-hidden rounded-[2.1rem] bg-night text-paper">
          <div className="flex items-center justify-between px-5 pb-2 pt-5">
            <div className="flex items-center gap-2">
              <img src="/assets/logo-icon.png" alt="" className="h-7 w-7 rounded-lg" />
              <span className="font-display text-sm font-semibold tracking-tight">Rupora</span>
            </div>
            <span className="rounded-full bg-brand-500/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-brand-300">
              Verified
            </span>
          </div>
          <div className="px-5 pt-4">
            <p className="text-[11px] uppercase tracking-widest text-mist">Wallet Balance</p>
            <p className="font-display text-3xl font-semibold tracking-tight">
              ₹24,500 <span className="text-sm text-mist">INR</span>
            </p>
          </div>
          <div className="mx-5 mt-4 rounded-2xl bg-pine p-4">
            <div className="flex items-center justify-between text-[11px] text-mist">
              <span>Current rate</span>
              <span className="inline-flex items-center gap-1 text-brand-300">
                <span className="h-1 w-1 animate-pulse rounded-full bg-brand-400" /> Live
              </span>
            </div>
            <div className="mt-1.5 flex items-center justify-between">
              <span className="text-sm font-medium">1 USDT</span>
              <span className="font-display text-sm font-semibold text-brand-300">≈ ₹110</span>
            </div>
          </div>
          <div className="px-5 pb-6 pt-4">
            <div className="rounded-full bg-gradient-to-r from-brand-500 to-brand-600 py-3 text-center text-sm font-semibold text-white">
              Exchange Now
            </div>
            <div className="mt-4 space-y-2.5">
              {[
                { t: "Exchange · 100 USDT", s: "Completed" },
                { t: "Verification check", s: "Passed" },
              ].map((row) => (
                <div key={row.t} className="flex items-center justify-between text-[11px]">
                  <span className="text-paper/60">{row.t}</span>
                  <span className="text-brand-300">{row.s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Hero() {
  const liveRate = useLiveRate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="home" ref={ref} className="relative overflow-hidden pb-16 pt-32 sm:pt-40 lg:pb-24" data-testid="hero-section">
      <motion.div
        style={{ y: glowY }}
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 right-[-15%] h-[560px] w-[560px] rounded-full bg-brand-600/20 blur-[130px]"
      />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-500/40 to-transparent" aria-hidden="true" />

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-7">
          <motion.div {...fadeUp(0.05)} className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5" data-testid="hero-eyebrow">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-400" />
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-300">Clean Fund Exchange</span>
          </motion.div>

          <h1 className="font-display text-[2.7rem] font-semibold leading-[1.02] tracking-tighter text-paper sm:text-6xl lg:text-[4.4rem]" data-testid="hero-heading">
            <MaskedLine delay={0.15}>Clean Fund Exchange,</MaskedLine>
            <MaskedLine delay={0.3} innerClassName="bg-gradient-to-r from-brand-300 via-brand-400 to-brand-600 bg-clip-text text-transparent">
              Built for Speed &amp; Trust
            </MaskedLine>
          </h1>

          <motion.p {...fadeUp(0.5)} className="mt-6 max-w-xl text-base leading-relaxed text-mist sm:text-lg" data-testid="hero-subtext">
            Exchange clean funds at competitive market rates with fast processing and a
            transparent process.
          </motion.p>

          <motion.div {...fadeUp(0.58)} className="mt-6 inline-flex flex-col gap-1.5 rounded-2xl border border-line bg-pine/70 px-5 py-4" data-testid="hero-rate-chip">
            <span className="font-display text-2xl font-semibold tracking-tight text-brand-300" data-testid="hero-rate-range">{rateRangeText(liveRate)} <span className="text-sm font-medium text-mist">/ USDT</span></span>
            <span className="text-xs text-mist/80">Indicative market rate • Rates may vary based on volume and market conditions</span>
          </motion.div>

          <motion.div {...fadeUp(0.68)} className="mt-8 flex flex-col gap-3.5 sm:flex-row sm:items-center">
            <a
              href="/login"
              data-testid="hero-get-started-button"
              className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-brand-500 to-brand-600 px-8 py-4 text-base font-semibold text-white shadow-xl shadow-brand-500/30 transition-all duration-300 hover:shadow-2xl hover:shadow-brand-500/40 hover:brightness-110"
            >
              Get Started
              <ArrowRight size={18} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5" />
            </a>
            <a
              href="#rates"
              data-testid="hero-view-rates-button"
              className="inline-flex items-center justify-center rounded-full border border-line px-8 py-4 text-base font-semibold text-paper transition-all duration-300 hover:border-brand-500/50 hover:text-brand-300"
            >
              View Rates
            </a>
          </motion.div>

          <motion.div {...fadeUp(0.8)} className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-mist" data-testid="hero-trust-row">
            <span className="inline-flex items-center gap-2"><ShieldCheck size={16} weight="duotone" className="text-brand-400" /> Verified process</span>
            <span className="inline-flex items-center gap-2"><Lightning size={16} weight="duotone" className="text-brand-400" /> Fast processing</span>
            <span className="inline-flex items-center gap-2"><LockKey size={16} weight="duotone" className="text-brand-400" /> Security-focused</span>
          </motion.div>
        </div>

        <div className="flex justify-center lg:col-span-5 lg:justify-end">
          <motion.div
            style={{ y: phoneY }}
            initial={{ opacity: 0, y: 60, rotate: 2 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ duration: 1.2, delay: 0.45, ease: EASE }}
          >
            <PhoneMockup />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
